import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    Switch,
    FormControlLabel,
    Checkbox,
    InputAdornment,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionIcon from '@material-ui/icons/Description'
import GetAppIcon from '@material-ui/icons/GetApp'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import EventIcon from '@material-ui/icons/Event'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import BusinessIcon from '@material-ui/icons/Business'
import CategoryIcon from '@material-ui/icons/Category'
import SecurityIcon from '@material-ui/icons/Security'
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar'
import BeachAccessIcon from '@material-ui/icons/BeachAccess'
import BuildIcon from '@material-ui/icons/Build'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import WarningIcon from '@material-ui/icons/Warning'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import EditIcon from '@material-ui/icons/Edit'
import CheckIcon from '@material-ui/icons/Check'
import ClearIcon from '@material-ui/icons/Clear'
import NotesIcon from '@material-ui/icons/Notes'
import ScheduleIcon from '@material-ui/icons/Schedule'
import { Container } from '../../components'
import {
    VendorCOI,
    VendorCOIStatus,
    UpdateVendorCOIRequest,
} from '../../hooks/useVendorCOIs'
import { Service } from '../../models/Services/types'
import { getServiceImageDetails } from '../../models/WorkOrder/services'
import { coiReviewCaption } from './utils'

interface VendorCOIViewModalProps {
    isOpen: boolean
    onClose: () => void
    vendorCOI: VendorCOI | null
    onUpdate?: (id: number, data: UpdateVendorCOIRequest) => void
    isUpdating?: boolean
    allServices?: Service[]
    onCheckCompliance?: (id: number) => void
    isCheckingCompliance?: boolean
    onSetComplianceStatus?: (
        id: number,
        status: 'COMPLIANT' | 'NON_COMPLIANT',
    ) => void
    isSettingComplianceStatus?: boolean
}

type EditableField =
    | 'effective_date'
    | 'expiration_date'
    | 'certificate_holder_name'
    | 'certificate_holder_address'
    | 'description_of_operations'
    | 'gl_each_occurrence'
    | 'gl_damage_to_rented_premises'
    | 'gl_med_exp'
    | 'gl_personal_adv_injury'
    | 'gl_general_aggregate'
    | 'gl_products_comp_op_agg'
    | 'auto_combined_single_limit'
    | 'auto_bodily_injury_per_person'
    | 'auto_bodily_injury_per_accident'
    | 'auto_property_damage'
    | 'umbrella_each_occurrence'
    | 'umbrella_aggregate'
    | 'wc_statutory_limits'
    | 'wc_el_each_accident'
    | 'wc_el_disease_ea_employee'
    | 'wc_el_disease_policy_limit'
    | 'services'

export const VendorCOIViewModal: React.FC<VendorCOIViewModalProps> = ({
    isOpen,
    onClose,
    vendorCOI,
    onUpdate,
    isUpdating,
    allServices = [],
    onCheckCompliance,
    isCheckingCompliance,
    onSetComplianceStatus,
    isSettingComplianceStatus,
}) => {
    const theme = useTheme()
    const [editingField, setEditingField] = useState<EditableField | null>(null)
    const [editValue, setEditValue] = useState<string>('')
    const [editBoolValue, setEditBoolValue] = useState<boolean>(false)
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([])
    const [failedIcons, setFailedIcons] = useState<Set<number>>(new Set())

    const handleIconError = useCallback((serviceId: number) => {
        setFailedIcons((prev) => new Set(prev).add(serviceId))
    }, [])

    // Reset editing state when modal closes or COI changes
    useEffect(() => {
        if (!isOpen) {
            setEditingField(null)
            setEditValue('')
            setEditBoolValue(false)
            setFailedIcons(new Set())
        }
    }, [isOpen, vendorCOI?.id])

    // Memoize formatters to prevent recreation on each render
    const formatDate = useCallback((dateString: string | null) => {
        if (!dateString) return '—'
        const [year, month, day] = dateString.split('T')[0].split('-')
        const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ]
        return `${months[parseInt(month, 10) - 1]} ${parseInt(
            day,
            10,
        )}, ${year}`
    }, [])

    const formatCurrency = useCallback((value: string | null) => {
        if (!value) return '—'
        const num = parseFloat(value)
        if (isNaN(num)) return value
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(num)
    }, [])

    const parseCurrencyToNumber = useCallback((value: string): string => {
        const cleaned = value.replace(/[$,]/g, '').trim()
        const num = parseFloat(cleaned)
        if (isNaN(num)) return ''
        return num.toFixed(2)
    }, [])

    // Memoize computed values
    const isPdf = useMemo(
        () => vendorCOI?.name.toLowerCase().endsWith('.pdf') ?? false,
        [vendorCOI?.name],
    )

    const viewerUrl = useMemo(() => {
        if (!vendorCOI?.file) return ''
        if (isPdf) {
            return vendorCOI.file
        }
        return `https://docs.google.com/gview?url=${encodeURIComponent(
            vendorCOI.file,
        )}&embedded=true`
    }, [vendorCOI?.file, isPdf])

    // Early return after hooks
    if (!vendorCOI) return null

    const handleDownload = () => {
        if (vendorCOI.file) {
            const link = window.document.createElement('a')
            link.href = vendorCOI.file
            link.download = vendorCOI.name
            link.click()
        }
    }

    const handleOpenInNewTab = () => {
        if (vendorCOI.file) {
            window.open(vendorCOI.file, '_blank')
        }
    }

    const getStatusDisplay = (status: VendorCOIStatus) => {
        switch (status) {
            case 'COMPLIANT':
                return {
                    label: 'Compliant',
                    color: theme.palette.success.main,
                    bgColor: theme.palette.success.light + '30',
                    icon: <VerifiedUserIcon style={{ fontSize: 16 }} />,
                }
            case 'NON_COMPLIANT':
                return {
                    label: 'Non-Compliant',
                    color: theme.palette.error.main,
                    bgColor: theme.palette.error.light + '30',
                    icon: <ReportProblemIcon style={{ fontSize: 16 }} />,
                }
            case 'EXPIRED':
                return {
                    label: 'Expired',
                    color: theme.palette.error.main,
                    bgColor: theme.palette.error.light + '30',
                    icon: <ReportProblemIcon style={{ fontSize: 16 }} />,
                }
            case 'EXPIRING_SOON':
                return {
                    label: 'Expiring Soon',
                    color: theme.palette.warning.main,
                    bgColor: theme.palette.warning.light + '30',
                    icon: <WarningIcon style={{ fontSize: 16 }} />,
                }
            case 'NOT_YET_EFFECTIVE':
                return {
                    label: 'Not Yet Effective',
                    color: theme.palette.info.main,
                    bgColor: theme.palette.info.light + '30',
                    icon: <ScheduleIcon style={{ fontSize: 16 }} />,
                }
            case 'PENDING_REVIEW':
            default:
                return {
                    label: 'Needs approval',
                    color: '#854d0e',
                    bgColor: '#fef9c3',
                    icon: <HourglassEmptyIcon style={{ fontSize: 16 }} />,
                }
        }
    }

    const statusDisplay = getStatusDisplay(vendorCOI.status)

    const startEditing = (field: EditableField) => {
        if (!onUpdate) return
        setEditingField(field)

        if (field === 'wc_statutory_limits') {
            setEditBoolValue(vendorCOI.wc_statutory_limits || false)
        } else if (field === 'services') {
            setSelectedServiceIds(vendorCOI.services.map((s) => s.id))
        } else if (field === 'effective_date') {
            // Format for date input (YYYY-MM-DD)
            if (vendorCOI.effective_date) {
                setEditValue(vendorCOI.effective_date.split('T')[0])
            } else {
                setEditValue('')
            }
        } else if (field === 'expiration_date') {
            // Format for date input (YYYY-MM-DD)
            if (vendorCOI.expiration_date) {
                setEditValue(vendorCOI.expiration_date.split('T')[0])
            } else {
                setEditValue('')
            }
        } else {
            const value = vendorCOI[field]
            setEditValue(value || '')
        }
    }

    const cancelEditing = () => {
        setEditingField(null)
        setEditValue('')
        setEditBoolValue(false)
        setSelectedServiceIds([])
    }

    const saveEdit = () => {
        if (!onUpdate || !editingField) return

        const updateData: UpdateVendorCOIRequest = {}

        if (editingField === 'wc_statutory_limits') {
            updateData.wc_statutory_limits = editBoolValue
        } else if (editingField === 'services') {
            updateData.service_ids = selectedServiceIds
        } else if (editingField === 'effective_date') {
            updateData.effective_date = editValue || undefined
        } else if (editingField === 'expiration_date') {
            updateData.expiration_date = editValue || undefined
        } else if (
            editingField.startsWith('gl_') ||
            editingField.startsWith('auto_') ||
            editingField.startsWith('umbrella_') ||
            editingField.startsWith('wc_el_')
        ) {
            const numValue = parseCurrencyToNumber(editValue)
            Object.assign(updateData, { [editingField]: numValue || null })
        } else {
            Object.assign(updateData, { [editingField]: editValue || null })
        }

        onUpdate(vendorCOI.id, updateData)
        cancelEditing()
    }

    const toggleService = (serviceId: number) => {
        setSelectedServiceIds((prev) =>
            prev.includes(serviceId)
                ? prev.filter((id) => id !== serviceId)
                : [...prev, serviceId],
        )
    }

    // Get service icon/color - uses icon (not src image)
    const getServiceVisual = (service: Service) => {
        const serviceDetails = getServiceImageDetails(
            service.id,
            service.service_category,
        )
        if (serviceDetails?.icon && !failedIcons.has(service.id)) {
            return (
                <img
                    src={serviceDetails.icon}
                    alt={service.name}
                    style={{
                        width: 20,
                        height: 20,
                        objectFit: 'contain',
                    }}
                    onError={() => handleIconError(service.id)}
                />
            )
        }
        return (
            <Box
                style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: service.color || '#9e9e9e',
                }}
            />
        )
    }

    // Render editable field with edit icon
    const renderEditableTextField = (
        label: string,
        field: EditableField,
        value: string | null,
        isCurrency: boolean = false,
    ) => {
        const isEditing = editingField === field
        const displayValue = isCurrency ? formatCurrency(value) : value || '—'

        return (
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.5}
            >
                <Typography variant="caption" color="textSecondary">
                    {label}
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    {isEditing ? (
                        <>
                            <TextField
                                size="small"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                variant="outlined"
                                style={{ width: 120 }}
                                InputProps={{
                                    style: { fontSize: 12, padding: '2px 8px' },
                                    startAdornment: isCurrency ? (
                                        <InputAdornment position="start">
                                            $
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveEdit()
                                    if (e.key === 'Escape') cancelEditing()
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={saveEdit}
                                disabled={isUpdating}
                            >
                                <CheckIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.success.main,
                                    }}
                                />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEditing}>
                                <ClearIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.error.main,
                                    }}
                                />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="caption"
                                style={{ fontWeight: 500 }}
                            >
                                {displayValue}
                            </Typography>
                            {onUpdate && (
                                <IconButton
                                    size="small"
                                    onClick={() => startEditing(field)}
                                    style={{ padding: 2, marginLeft: 4 }}
                                >
                                    <EditIcon
                                        style={{
                                            fontSize: 14,
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                </IconButton>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        )
    }

    // Render date field
    const renderEditableDateField = (
        label: string,
        icon: React.ReactNode,
        field: EditableField,
        value: string | null,
    ) => {
        const isEditing = editingField === field

        return (
            <Box mb={2}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={0.5}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: theme.spacing(1) }}
                    >
                        {icon}
                        <Typography variant="body2" color="textSecondary">
                            {label}
                        </Typography>
                    </Box>
                    {!isEditing && onUpdate && (
                        <IconButton
                            size="small"
                            onClick={() => startEditing(field)}
                            style={{ padding: 4 }}
                        >
                            <EditIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                        </IconButton>
                    )}
                </Box>
                {isEditing ? (
                    <Box
                        style={{ marginLeft: 26, gap: 8 }}
                        display="flex"
                        alignItems="center"
                    >
                        <TextField
                            type="date"
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                        <IconButton
                            size="small"
                            onClick={saveEdit}
                            disabled={isUpdating}
                        >
                            <CheckIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.success.main,
                                }}
                            />
                        </IconButton>
                        <IconButton size="small" onClick={cancelEditing}>
                            <ClearIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.error.main,
                                }}
                            />
                        </IconButton>
                    </Box>
                ) : (
                    <Typography
                        variant="body1"
                        style={{ fontWeight: 500, marginLeft: 26 }}
                    >
                        {formatDate(value)}
                    </Typography>
                )}
            </Box>
        )
    }

    // Render boolean field
    const renderBooleanField = (
        label: string,
        field: EditableField,
        value: boolean | null,
    ) => {
        const isEditing = editingField === field

        return (
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.5}
            >
                <Typography variant="caption" color="textSecondary">
                    {label}
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    {isEditing ? (
                        <>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editBoolValue}
                                        onChange={(e) =>
                                            setEditBoolValue(e.target.checked)
                                        }
                                        size="small"
                                        color="primary"
                                    />
                                }
                                label={editBoolValue ? 'Yes' : 'No'}
                                style={{ margin: 0 }}
                            />
                            <IconButton
                                size="small"
                                onClick={saveEdit}
                                disabled={isUpdating}
                            >
                                <CheckIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.success.main,
                                    }}
                                />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEditing}>
                                <ClearIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.error.main,
                                    }}
                                />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="caption"
                                style={{ fontWeight: 500 }}
                            >
                                {value ? 'Yes' : '—'}
                            </Typography>
                            {onUpdate && (
                                <IconButton
                                    size="small"
                                    onClick={() => startEditing(field)}
                                    style={{ padding: 2, marginLeft: 4 }}
                                >
                                    <EditIcon
                                        style={{
                                            fontSize: 14,
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                </IconButton>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        )
    }

    // Render large text field
    const renderEditableTextArea = (
        label: string,
        icon: React.ReactNode,
        field: EditableField,
        value: string | null,
    ) => {
        const isEditing = editingField === field

        return (
            <Box mb={3}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={0.5}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: theme.spacing(1) }}
                    >
                        {icon}
                        <Typography variant="body2" color="textSecondary">
                            {label}
                        </Typography>
                    </Box>
                    {!isEditing && onUpdate && (
                        <IconButton
                            size="small"
                            onClick={() => startEditing(field)}
                            style={{ padding: 4 }}
                        >
                            <EditIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                        </IconButton>
                    )}
                </Box>
                {isEditing ? (
                    <Box style={{ marginLeft: 26 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            variant="outlined"
                            autoFocus
                        />
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            mt={1}
                            style={{ gap: 4 }}
                        >
                            <Button
                                size="small"
                                onClick={cancelEditing}
                                style={{ minWidth: 0, padding: '2px 8px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={saveEdit}
                                disabled={isUpdating}
                                style={{ minWidth: 0, padding: '2px 8px' }}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Typography
                        variant="body2"
                        style={{
                            marginLeft: 26,
                            fontStyle: value ? 'normal' : 'italic',
                            color: value
                                ? theme.palette.text.primary
                                : theme.palette.text.secondary,
                        }}
                    >
                        {value || 'Not specified'}
                    </Typography>
                )}
            </Box>
        )
    }

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                style: {
                    height: '90vh',
                    maxHeight: '90vh',
                    width: '95vw',
                    maxWidth: 1400,
                },
            }}
        >
            {/* Header */}
            <Container
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: 'row',
                    padding: theme.spacing(2),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: theme.spacing(2) }}
                >
                    <Box
                        style={{
                            height: 40,
                            width: 40,
                            borderRadius: theme.spacing(1),
                            backgroundColor: theme.palette.secondary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.secondary.contrastText,
                        }}
                    >
                        <DescriptionIcon />
                    </Box>
                    <Box>
                        <Typography variant="h6">{vendorCOI.name}</Typography>
                        {vendorCOI.vendor && (
                            <Typography variant="body2" color="textSecondary">
                                {vendorCOI.vendor.name}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: theme.spacing(2) }}
                >
                    <Box>
                        <Tooltip
                            title={
                                vendorCOI.status === 'NON_COMPLIANT' &&
                                vendorCOI.compliance_notes ? (
                                    <Typography
                                        variant="body2"
                                        style={{ whiteSpace: 'pre-wrap' }}
                                    >
                                        {vendorCOI.compliance_notes}
                                    </Typography>
                                ) : (
                                    ''
                                )
                            }
                            arrow
                            placement="bottom"
                            interactive
                        >
                            <Chip
                                icon={statusDisplay.icon}
                                label={statusDisplay.label}
                                size="small"
                                style={{
                                    backgroundColor: statusDisplay.bgColor,
                                    color: statusDisplay.color,
                                    fontWeight: 500,
                                    cursor:
                                        vendorCOI.status === 'NON_COMPLIANT' &&
                                        vendorCOI.compliance_notes
                                            ? 'help'
                                            : 'default',
                                }}
                            />
                        </Tooltip>
                        {(vendorCOI.status === 'PENDING_REVIEW' ||
                            coiReviewCaption(vendorCOI)) && (
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ display: 'block', marginTop: 4 }}
                            >
                                {vendorCOI.status === 'PENDING_REVIEW'
                                    ? 'Waiting for your approval'
                                    : coiReviewCaption(vendorCOI)}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Container>

            {/* Content */}
            <DialogContent
                style={{
                    padding: 0,
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {/* Left: Details */}
                <Box
                    style={{
                        width: 400,
                        minWidth: 400,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(3),
                        overflowY: 'auto',
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    {/* Vendor COI Details */}
                    <Typography
                        variant="overline"
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            display: 'block',
                        }}
                    >
                        Vendor COI Details
                    </Typography>

                    {/* Vendor */}
                    <Box mb={2}>
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={0.5}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <BusinessIcon
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Vendor
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, marginLeft: 26 }}
                        >
                            {vendorCOI.vendor?.name || '—'}
                        </Typography>
                    </Box>

                    {/* Effective Date */}
                    {renderEditableDateField(
                        'Effective Date',
                        <EventIcon
                            style={{
                                fontSize: 18,
                                color: theme.palette.text.secondary,
                            }}
                        />,
                        'effective_date',
                        vendorCOI.effective_date,
                    )}

                    {/* Expiration Date */}
                    {renderEditableDateField(
                        'Expiration Date',
                        <ScheduleIcon
                            style={{
                                fontSize: 18,
                                color: theme.palette.text.secondary,
                            }}
                        />,
                        'expiration_date',
                        vendorCOI.expiration_date,
                    )}

                    {/* Created / Modified */}
                    <Box mb={2}>
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={0.5}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <EventIcon
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Created
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, marginLeft: 26 }}
                        >
                            {formatDate(vendorCOI.created_date)}
                        </Typography>
                    </Box>

                    <Box mb={2}>
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={0.5}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <AutorenewIcon
                                style={{
                                    fontSize: 18,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                            <Typography variant="body2" color="textSecondary">
                                Last Modified
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, marginLeft: 26 }}
                        >
                            {formatDate(vendorCOI.modified_date)}
                        </Typography>
                    </Box>

                    {/* Services Section */}
                    <Box
                        mb={3}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(2),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1.5}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: theme.spacing(1) }}
                            >
                                <CategoryIcon
                                    style={{
                                        fontSize: 18,
                                        color: theme.palette.secondary.main,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 600 }}
                                >
                                    Services
                                </Typography>
                                <Chip
                                    label={vendorCOI.services.length}
                                    size="small"
                                    style={{
                                        height: 20,
                                        fontSize: 11,
                                        backgroundColor:
                                            theme.palette.secondary.main,
                                        color: '#fff',
                                    }}
                                />
                            </Box>
                            {onUpdate && editingField !== 'services' && (
                                <IconButton
                                    size="small"
                                    onClick={() => startEditing('services')}
                                    style={{ padding: 4 }}
                                >
                                    <EditIcon
                                        style={{
                                            fontSize: 16,
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                </IconButton>
                            )}
                        </Box>

                        {editingField === 'services' ? (
                            <Box>
                                <Box
                                    style={{
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        backgroundColor: '#fff',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: theme.spacing(0.5),
                                        padding: theme.spacing(1),
                                    }}
                                >
                                    {allServices.map((service) => (
                                        <Box
                                            key={service.id}
                                            display="flex"
                                            alignItems="center"
                                            style={{
                                                gap: 10,
                                                padding: '6px 4px',
                                                cursor: 'pointer',
                                                borderRadius: 4,
                                                backgroundColor: selectedServiceIds.includes(
                                                    service.id,
                                                )
                                                    ? theme.palette.secondary
                                                          .light + '20'
                                                    : 'transparent',
                                            }}
                                            onClick={() =>
                                                toggleService(service.id)
                                            }
                                        >
                                            <Checkbox
                                                checked={selectedServiceIds.includes(
                                                    service.id,
                                                )}
                                                size="small"
                                                color="secondary"
                                                style={{ padding: 0 }}
                                            />
                                            {getServiceVisual(service)}
                                            <Typography variant="body2">
                                                {service.name}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    mt={1.5}
                                    style={{ gap: 8 }}
                                >
                                    <Button
                                        size="small"
                                        onClick={cancelEditing}
                                        variant="outlined"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="secondary"
                                        onClick={saveEdit}
                                        disabled={isUpdating}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                {vendorCOI.services.length > 0 ? (
                                    <Box
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: theme.spacing(0.75),
                                            maxHeight: 150,
                                            overflowY: 'auto',
                                        }}
                                    >
                                        {vendorCOI.services.map((service) => (
                                            <Box
                                                key={service.id}
                                                display="flex"
                                                alignItems="center"
                                                style={{
                                                    gap: theme.spacing(1.5),
                                                    padding: theme.spacing(
                                                        0.75,
                                                        1,
                                                    ),
                                                    backgroundColor: '#fff',
                                                    borderRadius: theme.spacing(
                                                        0.5,
                                                    ),
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Box
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: 4,
                                                        backgroundColor:
                                                            service.color +
                                                            '20',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {getServiceVisual(service)}
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    style={{ fontWeight: 500 }}
                                                >
                                                    {service.name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{
                                            fontStyle: 'italic',
                                            textAlign: 'center',
                                            padding: theme.spacing(1),
                                        }}
                                    >
                                        No services assigned
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>

                    {/* Certificate Holder */}
                    {renderEditableTextArea(
                        'Certificate Holder',
                        <BusinessIcon
                            style={{
                                fontSize: 18,
                                color: theme.palette.text.secondary,
                            }}
                        />,
                        'certificate_holder_name',
                        vendorCOI.certificate_holder_name,
                    )}

                    {renderEditableTextArea(
                        'Certificate Holder Address',
                        <BusinessIcon
                            style={{
                                fontSize: 18,
                                color: theme.palette.text.secondary,
                            }}
                        />,
                        'certificate_holder_address',
                        vendorCOI.certificate_holder_address,
                    )}

                    {renderEditableTextArea(
                        'Description of Operations',
                        <NotesIcon
                            style={{
                                fontSize: 18,
                                color: theme.palette.text.secondary,
                            }}
                        />,
                        'description_of_operations',
                        vendorCOI.description_of_operations,
                    )}

                    {/* Coverage Requirements */}
                    <Typography
                        variant="overline"
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            display: 'block',
                            marginTop: theme.spacing(2),
                        }}
                    >
                        Coverage Amounts
                    </Typography>

                    {/* General Liability */}
                    <Box
                        mb={2}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(1.5),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={1}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <SecurityIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.primary.main,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                General Liability
                            </Typography>
                        </Box>
                        <Box style={{ marginLeft: 24 }}>
                            {renderEditableTextField(
                                'Each Occurrence',
                                'gl_each_occurrence',
                                vendorCOI.gl_each_occurrence,
                                true,
                            )}
                            {renderEditableTextField(
                                'Damage to Rented Premises',
                                'gl_damage_to_rented_premises',
                                vendorCOI.gl_damage_to_rented_premises,
                                true,
                            )}
                            {renderEditableTextField(
                                'Medical Expense',
                                'gl_med_exp',
                                vendorCOI.gl_med_exp,
                                true,
                            )}
                            {renderEditableTextField(
                                'Personal & Adv. Injury',
                                'gl_personal_adv_injury',
                                vendorCOI.gl_personal_adv_injury,
                                true,
                            )}
                            {renderEditableTextField(
                                'General Aggregate',
                                'gl_general_aggregate',
                                vendorCOI.gl_general_aggregate,
                                true,
                            )}
                            {renderEditableTextField(
                                'Products/Comp Ops Agg',
                                'gl_products_comp_op_agg',
                                vendorCOI.gl_products_comp_op_agg,
                                true,
                            )}
                        </Box>
                    </Box>

                    {/* Auto Liability */}
                    <Box
                        mb={2}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(1.5),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={1}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <DirectionsCarIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.info.main,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                Auto Liability
                            </Typography>
                        </Box>
                        <Box style={{ marginLeft: 24 }}>
                            {renderEditableTextField(
                                'Combined Single Limit',
                                'auto_combined_single_limit',
                                vendorCOI.auto_combined_single_limit,
                                true,
                            )}
                            {renderEditableTextField(
                                'Bodily Injury (Per Person)',
                                'auto_bodily_injury_per_person',
                                vendorCOI.auto_bodily_injury_per_person,
                                true,
                            )}
                            {renderEditableTextField(
                                'Bodily Injury (Per Accident)',
                                'auto_bodily_injury_per_accident',
                                vendorCOI.auto_bodily_injury_per_accident,
                                true,
                            )}
                            {renderEditableTextField(
                                'Property Damage',
                                'auto_property_damage',
                                vendorCOI.auto_property_damage,
                                true,
                            )}
                        </Box>
                    </Box>

                    {/* Umbrella */}
                    <Box
                        mb={2}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(1.5),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={1}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <BeachAccessIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.warning.main,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                Umbrella / Excess Liability
                            </Typography>
                        </Box>
                        <Box style={{ marginLeft: 24 }}>
                            {renderEditableTextField(
                                'Each Occurrence',
                                'umbrella_each_occurrence',
                                vendorCOI.umbrella_each_occurrence,
                                true,
                            )}
                            {renderEditableTextField(
                                'Aggregate',
                                'umbrella_aggregate',
                                vendorCOI.umbrella_aggregate,
                                true,
                            )}
                        </Box>
                    </Box>

                    {/* Workers' Comp */}
                    <Box
                        mb={2}
                        style={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(1.5),
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            mb={1}
                            style={{ gap: theme.spacing(1) }}
                        >
                            <BuildIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.error.main,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                Workers&apos; Compensation
                            </Typography>
                        </Box>
                        <Box style={{ marginLeft: 24 }}>
                            {renderBooleanField(
                                'Statutory Limits',
                                'wc_statutory_limits',
                                vendorCOI.wc_statutory_limits,
                            )}
                            {renderEditableTextField(
                                'E.L. Each Accident',
                                'wc_el_each_accident',
                                vendorCOI.wc_el_each_accident,
                                true,
                            )}
                            {renderEditableTextField(
                                'E.L. Disease (Each Employee)',
                                'wc_el_disease_ea_employee',
                                vendorCOI.wc_el_disease_ea_employee,
                                true,
                            )}
                            {renderEditableTextField(
                                'E.L. Disease (Policy Limit)',
                                'wc_el_disease_policy_limit',
                                vendorCOI.wc_el_disease_policy_limit,
                                true,
                            )}
                        </Box>
                    </Box>

                    {/* Compliance Notes */}
                    {vendorCOI.compliance_notes && (
                        <Box mb={3}>
                            <Typography
                                variant="overline"
                                style={{
                                    color: theme.palette.text.secondary,
                                    fontWeight: 600,
                                    marginBottom: theme.spacing(1),
                                    display: 'block',
                                }}
                            >
                                Compliance Notes
                            </Typography>
                            <Box
                                style={{
                                    backgroundColor:
                                        theme.palette.warning.light + '20',
                                    borderRadius: theme.spacing(1),
                                    padding: theme.spacing(2),
                                    border: `1px solid ${theme.palette.warning.main}`,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    style={{ lineHeight: 1.7 }}
                                >
                                    {vendorCOI.compliance_notes}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* AI Summary */}
                    <Box
                        style={{
                            backgroundColor: theme.palette.info.light + '15',
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(2),
                            border: `1px solid ${theme.palette.info.light}`,
                            marginTop: theme.spacing(2),
                        }}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{
                                gap: theme.spacing(1),
                                marginBottom: theme.spacing(1.5),
                            }}
                        >
                            <Box
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 6,
                                    backgroundColor: theme.palette.info.main,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <AutorenewIcon
                                    style={{ fontSize: 16, color: '#fff' }}
                                />
                            </Box>
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                AI Summary
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            style={{
                                lineHeight: 1.7,
                                fontStyle: vendorCOI.ai_summary
                                    ? 'normal'
                                    : 'italic',
                                color: vendorCOI.ai_summary
                                    ? theme.palette.text.primary
                                    : theme.palette.text.secondary,
                                whiteSpace: 'pre-wrap',
                            }}
                        >
                            {vendorCOI.ai_summary ||
                                'No summary available yet. The document may still be processing.'}
                        </Typography>
                    </Box>
                </Box>

                {/* Right: Document Viewer */}
                <Box style={{ flex: 1, minWidth: 0 }}>
                    {vendorCOI.file ? (
                        <iframe
                            src={viewerUrl}
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                backgroundColor: theme.palette.background.paper,
                            }}
                            title={vendorCOI.name}
                        />
                    ) : (
                        <Box
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            bgcolor={theme.palette.grey[100]}
                        >
                            <Typography color="textSecondary">
                                No document available
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions
                style={{
                    padding: theme.spacing(1.5, 2),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'space-between',
                    gap: theme.spacing(2),
                }}
            >
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    {onCheckCompliance && (
                        <Button
                            variant="outlined"
                            color="default"
                            onClick={() => onCheckCompliance(vendorCOI.id)}
                            disabled={
                                isCheckingCompliance ||
                                isSettingComplianceStatus
                            }
                            startIcon={
                                isCheckingCompliance ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : (
                                    <VerifiedUserIcon
                                        style={{ fontSize: 18 }}
                                    />
                                )
                            }
                            style={{
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {isCheckingCompliance
                                ? 'Checking...'
                                : 'Check compliance'}
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="default"
                        onClick={handleOpenInNewTab}
                        startIcon={<OpenInNewIcon style={{ fontSize: 18 }} />}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Open in new tab
                    </Button>
                    <Button
                        variant="outlined"
                        color="default"
                        onClick={handleDownload}
                        startIcon={<GetAppIcon style={{ fontSize: 18 }} />}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Download
                    </Button>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Button
                        onClick={onClose}
                        style={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        Close
                    </Button>
                    {onSetComplianceStatus &&
                        vendorCOI.status !== 'NON_COMPLIANT' && (
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    onSetComplianceStatus(
                                        vendorCOI.id,
                                        'NON_COMPLIANT',
                                    )
                                }
                                disabled={isSettingComplianceStatus}
                                style={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    color: '#991b1b',
                                    borderColor: '#fecaca',
                                    backgroundColor: '#fff',
                                }}
                            >
                                Deny
                            </Button>
                        )}
                    {onSetComplianceStatus && vendorCOI.status !== 'COMPLIANT' && (
                        <Button
                            variant="contained"
                            color="primary"
                            disableElevation
                            onClick={() =>
                                onSetComplianceStatus(vendorCOI.id, 'COMPLIANT')
                            }
                            disabled={isSettingComplianceStatus}
                            startIcon={
                                isSettingComplianceStatus ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : undefined
                            }
                            style={{
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            Approve
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    )
}
